import {
  Button,
  ButtonColorEnum,
  DrawerForm,
  DrawerSizeEnum,
  FontColorEnum,
  FontWeightEnum,
  Typography,
} from '@acceso/design-system';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { HighlightContainer } from '@/components/FormFields/HighlightContainer';
import { InputLogos } from '@/components/FormFields/InputLogos';
import { DrawerFormDataType, FormTypeEnum } from '@/types';

import { useCNMCFilesUploadFormState } from '../../hooks/useCNMCFilesUploadFormState';
import { useCNMCFileUploadsController } from '../../hooks/useCNMCFileUploadsController';
import { CNMC, CNMCFilesUploadForm, CNMCForm } from '../../types';

import { CNMCFilesTable } from './components/CNMCFilesTable/CNMCFilesTable';

export type TCNMCDrawerFormProps = {
  isOpen: boolean;
  data?: DrawerFormDataType<CNMC> | null;
  onClose: () => void;
  onSubmit: (data: CNMCForm) => void;
  isMutating: boolean;
};

export const CNMCFilesUploadDrawer = ({
  data,
  isOpen,
  onClose,
  isMutating,
}: TCNMCDrawerFormProps) => {
  const { t } = useTranslation();
  const { form } = useCNMCFilesUploadFormState();
  const { uploadFile, addFile } = useCNMCFileUploadsController();

  useEffect(() => {
    if (!isOpen) {
      form.reset();
    }
  }, [form.reset, isOpen]);

  useEffect(() => {
    if (!data?.data) return;

    const formData: Partial<CNMCFilesUploadForm> = {};

    if (data.type === FormTypeEnum.UPDATE) {
      form.reset(formData);
    }
  }, [data]);

  const files = data?.data?.files;

  useEffect(() => {
    if (!files) {
      return;
    }

    files.forEach(file => addFile(file));
  }, [files]);

  return (
    <DrawerForm
      title={
        data?.type === FormTypeEnum.CREATE
          ? t('Subir archivos CNMC')
          : t('Editar datos del informe CNMC')
      }
      isOpen={isOpen}
      onCancel={onClose}
      isMutating={isMutating}
      size={DrawerSizeEnum.MD}
      labels={{
        cancel: t('Cancelar'),
      }}
    >
      <HighlightContainer title={t('Escaleta')}>
        <InputLogos
          form={form}
          name="textFiles"
          newFiles
          label={t('Subir archivo CSV para su validación')}
          accept=".csv"
          hidePreview
        />
      </HighlightContainer>

      <HighlightContainer title={t('Denominaciones de empresas')}>
        <InputLogos
          form={form}
          name="companyActivityFiles"
          newFiles
          label={t('Subir archivo CSV (company_activity_list.csv)')}
          accept=".csv"
          hidePreview
        />
      </HighlightContainer>

      <HighlightContainer title={t('Archivos de vídeo')}>
        <InputLogos
          form={form}
          name="videoFiles"
          newFiles
          label={t('Seleccionar archivos MKV y SRT')}
          accept=".mkv,.srt"
          hidePreview
        />
      </HighlightContainer>

      <Button
        className="mb-2 ml-auto"
        color={ButtonColorEnum.DEFAULT_GHOST}
        onClick={() => {
          const cnmcId = data?.data?.id;

          if (!cnmcId) {
            return;
          }

          void (async () => {
            const { videoFiles = [], textFiles = [], companyActivityFiles = [] } = form.getValues();
            const allFiles = [...textFiles, ...companyActivityFiles, ...videoFiles];
            const totalFiles = allFiles.length;
            let currentFileIndex = 0;

            for (const file of allFiles) {
              await uploadFile(file, cnmcId, currentFileIndex++, totalFiles);
            }
          })();
          form.reset();
        }}
      >
        Cargar
      </Button>

      <Typography color={FontColorEnum.Dark10} weight={FontWeightEnum.Semibold}>
        Ficheros
      </Typography>

      <div className="h-fit w-full">
        <CNMCFilesTable cnmcId={data?.data?.id} />
      </div>
    </DrawerForm>
  );
};
